import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fill = (tpl, vars) => (tpl || "").replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");

export const ReplyTemplates = ({ lead }) => {
  const [lang, setLang] = useState(lead.lang === "en" ? "en" : "fr");
  const { data: s } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  if (!s?.reply_templates?.length) return null;
  const vars = {
    name: (lead.name || "").trim().split(/\s+/)[0] || lead.name || "",
    vehicle: lead.vehicle || (lang === "fr" ? "votre demande" : "your request"),
    signature: s.reply_signature || "",
  };
  const phone = (lead.whatsapp || lead.phone || "").replace(/\D/g, "");
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <div className="mt-8 border-t border-white/10 pt-6" data-testid="reply-templates">
      <div className="mb-4 flex items-center justify-between">
        <p className="mono text-[0.58rem] text-[#8b95a5]">Reply templates</p>
        <div className="mono flex gap-1 text-[0.6rem]" data-testid="reply-lang-toggle">
          {["fr", "en"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`reply-lang-${l}`} className={`px-2 py-1 ${lang === l ? "bg-white/10 text-white" : "text-[#6f7986] hover:text-white"}`}>{l.toUpperCase()}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {s.reply_templates.map((t) => {
          const body = fill(t.body?.[lang], vars);
          const subject = fill(t.subject?.[lang], vars);
          return (
            <div key={t.id} className="admin-card p-3" data-testid={`reply-template-${t.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm">{t.name?.[lang] || t.name?.fr}</span>
                <div className="flex gap-1">
                  {phone && (
                    <a href={`https://wa.me/${phone}?text=${encodeURIComponent(body)}`} target="_blank" rel="noreferrer" title="Send on WhatsApp" data-testid={`reply-whatsapp-${t.id}`}
                       className="flex h-8 w-8 items-center justify-center rounded bg-[#25d366]/15 text-[#25d366] hover:bg-[#25d366]/30"><MessageCircle size={14} /></a>
                  )}
                  {lead.email && (
                    <a href={`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} title="Send by email" data-testid={`reply-email-${t.id}`}
                       className="flex h-8 w-8 items-center justify-center rounded bg-white/5 text-[#c7ccd4] hover:bg-white/10"><Mail size={14} /></a>
                  )}
                  <button onClick={() => copy(body)} title="Copy text" data-testid={`reply-copy-${t.id}`} className="flex h-8 w-8 items-center justify-center rounded bg-white/5 text-[#c7ccd4] hover:bg-white/10"><Copy size={14} /></button>
                </div>
              </div>
              <p className="mt-2 line-clamp-2 text-xs text-[#8b95a5]">{body.replace(/\s*\n+\s*/g, " · ")}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
